import { Injectable }                    from '@angular/core';
import { ModalDismissReasons, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService }              from '@ngx-translate/core';

import { ApiService }                          from '../api/api.service';
import { CompanyService }                      from '../companies/company.service';
import { CustomerModel }                       from '../customers/models/customer';
import { SessionService }                      from '../sessions/session.service';
import { Table }                               from '../table/types/table';
import { TableColumn }                         from '../table/types/table-column';
import { VehicleVisualModel }                  from './typings/vehicle-visuals';
import { VehicleVisualsCatalogModalComponent } from './vehicle-visuals.catalog-modal.component';
import { VehicleVisualsDocumentModel }         from './typings/vehicle-visuals.document';
import { VehicleVisualsEmailComponent }        from './vehicle-visuals.email.component';
import { VehicleVisualsLevel }                 from './typings/vehicle-visuals.level';
import { VehicleVisualsPlayerComponent }       from './vehicle-visuals.player.component';
import { VehicleVisualsPreferencesModel }      from './typings/vehicle-visuals.preferences';
import { VehicleVisualsSettingsModel }         from './typings/vehicle-visuals.settings';

import {
  VehicleVisualsCatalogSelectionModel,
  VehicleVisualsSelectionModel
} from './typings/vehicle-visuals.selection';

@Injectable({
  providedIn: 'root'
})
export class VehicleVisualsService {
  constructor (
    private apiService      : ApiService,
    private companyService  : CompanyService,
    private modalService    : NgbModal,
    private sessionService  : SessionService,
    private translateService: TranslateService
  ) {}

  attachToInspection ( inspection : VehicleVisualsDocumentModel, visual : VehicleVisualModel, interactive = false ) : Promise<any> {
    return this.getInteractiveUrl(visual, interactive)
      .then(url => this.apiService.post('/inspection/attach_vehicle_visual', {
        description  : visual.description || visual.description_en_AU,
        inspection_id: inspection.id,
        name         : visual.title || visual.en_AU,
        url
      }));
  }

  attachToInvoice ( invoice : VehicleVisualsDocumentModel, visual : VehicleVisualModel, interactive = false ) : Promise<any> {
    return this.getInteractiveUrl(visual, interactive)
      .then(url => this.apiService.post('/invoice/attach_vehicle_visual', {
        description: visual.description || visual.description_en_AU,
        invoice_id : invoice.id,
        name       : visual.title || visual.en_AU,
        url
      }));
  }

  attachToProduct ( interactive = false ) : Promise<VehicleVisualsSelectionModel | null> {
    const modal = this.modalService.open(VehicleVisualsCatalogModalComponent, {
      keyboard   : true,
      windowClass: 'x-lg-modal'
    });

    modal.componentInstance.interactive = interactive;

    return this.resolveDismissed(modal.result);
  }

  email ( customer : CustomerModel, visual : VehicleVisualModel ) : Promise<boolean> {
    if ( !this.companyService.hasIntegration('vehicle-visuals') ) {
      return this.upgrade().then(() => false);
    }

    return this.getCustomerUrl(visual)
      .then(url => {
        const modal = this.modalService.open(VehicleVisualsEmailComponent, {
          keyboard   : true,
          windowClass: 'lg-modal'
        });

        modal.componentInstance.customer = customer;
        modal.componentInstance.url      = url;
        modal.componentInstance.visual   = visual;

        return this.resolveDismissed(modal.result);
      })
      .then(sent => !!sent);
  }

  emptySelection () : VehicleVisualsCatalogSelectionModel {
    return {
      defect: null,
      part  : null,
      system: null
    };
  }

  getCatalog ( interactive = false ) : Promise<Array<VehicleVisualModel>> {
    return this.apiService.get('/vehicle_visuals/get_animation_details', interactive)
      .then(( catalog : Record<string, any> | null ) => catalog?.status === false
        ? Promise.reject(catalog)
        : this.convertToArray(catalog));
  }

  getCustomerUrl ( visual : VehicleVisualModel ) : Promise<string> {
    return this.apiService.get('/vehicle_visuals/get_dynamic_generated_url', visual.part_id);
  }

  getInteractiveUrl ( visual : VehicleVisualModel, interactive = false ) : Promise<string> {
    return this.apiService.get('/vehicle_visuals/interactive_animations', [
      visual.part_id,
      interactive
        ? 1
        : 0
    ]).then(( data : any ) => data.url);
  }

  getRootUrl ( visual : VehicleVisualModel, interactive = false ) : Promise<string> {
    const flag = interactive
      ? 1
      : 0;

    return Promise.resolve(`${ this.sessionService.company().vehicle_visuals_root_url }&part_id=${ visual.part_id }&is_interactive=${ flag }`);
  }

  getSettings () : Promise<VehicleVisualsSettingsModel> {
    return this.apiService.get('/company');
  }

  getUserPreferences () : Promise<VehicleVisualsPreferencesModel> {
    return this.apiService.get('/vehicle_visuals/get_user_preferences')
      .then(preferences => preferences?.data);
  }

  launch ( visual : VehicleVisualModel, interactive = false ) : Promise<any> {
    return this.getRootUrl(visual, interactive)
      .then(url => this.play(url, visual.title || visual.en_AU));
  }

  play ( url : string, title? : string ) : Promise<any> {
    const playUrl  = this.withAutoPlay(url);
    const modalRef = this.modalService.open(VehicleVisualsPlayerComponent, {
      windowClass: this.modalWindowClass()
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.url   = playUrl;

    return this.resolveDismissed(modalRef.result);
  }

  saveSettings ( settings : VehicleVisualsSettingsModel ) : Promise<any> {
    return this.apiService.patch('/company', settings, 'company')
      .then(() => this.sessionService.refresh());
  }

  // WS-7262: the term goes out raw; ApiService already encodes the URL.
  search ( query : string ) : Promise<Array<VehicleVisualModel>> {
    return this.apiService.get('/vehicle_visuals/search_animations', query)
      .then(( results : Record<string, any> | null ) => this.convertToArray(results));
  }

  searchResultsTableSettings ( columns : Array<TableColumn> = [] ) : Table {
    return {
      columns: [
        {
          classes: 'text-left',
          field  : 'title',
          header : {
            text: this.translateService.instant('VEHICLE_VISUALS_SPACE.PART')
          }
        },
        {
          classes: 'text-left',
          field  : 'description',
          header : {
            text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
          }
        },
        ...columns
      ],
      empty: {
        text: this.translateService.instant('GENERAL_SPACE.STATUS.NO_RESULTS')
      },
      pagination: {
        enabled: false
      },
      sorting: {
        enabled: false
      }
    };
  }

  selectLevel (
    selection : VehicleVisualsCatalogSelectionModel,
    level     : VehicleVisualsLevel,
    visual    : VehicleVisualModel | null
  ) : VehicleVisualsCatalogSelectionModel {
    const next = {
      ...selection,
      [level]: visual
    };

    if ( level !== 'defect' ) {
      next.defect = null;
    }

    if ( level === 'system' ) {
      next.part = null;
    }

    return next;
  }

  subscribe () : Promise<void> {
    return this.openExternalUrl('/vehicle_visuals/subscribe_url');
  }

  upgrade () : Promise<void> {
    return this.openExternalUrl('/vehicle_visuals/upgrade_url');
  }

  // Vehicle Visuals answers a failed request with an error string or a status object, not a catalog.
  private convertToArray ( data : Record<string, any> | null ) : Array<VehicleVisualModel> {
    if ( !data || typeof data !== 'object' || data.status === false ) {
      return [];
    }

    return Object.entries(data)
      .map(( [ key, item ] ) => ({
        ...item,
        key,
        ...(item.children
          ? {
              children: this.convertToArray(item.children)
            }
          : {})
      }));
  }

  /**
   * Inspection-bundle customers see attachments from a short-URL vendor share
   * page whose chrome dwarfs the player at our default modal size. Render the
   * modal full-screen on that bundle so the player fills the viewport.
   */
  private modalWindowClass () : string {
    const base = 'vehicle-visuals-player-modal';

    return window.location.pathname.includes('inspection.html')
      ? `${ base } vehicle-visuals-player-modal-fullscreen`
      : base;
  }

  /**
   * WS-6645. The replace alone only ever rewrites a parameter that is already present, and
   * nothing in either repository adds one: every Vehicle Visuals URL stored in production
   * carries a query string and none contains auto_play, so the animation never auto started
   * for any workshop. WS-6323 built that behaviour and WS-6486 reverted the append to stop
   * a bare short URL such as srvc.video/aZk2q being turned into an invalid address.
   *
   * Appending only when the URL already has a query string delivers WS-6323 without
   * recreating that regression, because a bare short URL has no question mark and is
   * returned untouched.
   */
  private withAutoPlay ( url : string ) : string {
    if (/auto_play=\d+/.test(url)) {
      return url.replace(/auto_play=\d+/, 'auto_play=1');
    }

    return url.includes('?') ? `${ url }&auto_play=1` : url;
  }

  private isDismiss ( reason : any ) : boolean {
    return reason === undefined
      || reason === ModalDismissReasons.BACKDROP_CLICK
      || reason === ModalDismissReasons.ESC;
  }

  private openExternalUrl ( path : string ) : Promise<void> {
    return this.apiService.get(path)
      .then(( url : string ) => {
        window.open(url, '_blank', 'noopener');
      });
  }

  private resolveDismissed<T> ( result : Promise<T> ) : Promise<T | null> {
    return result.catch(( reason : any ) => {
      if ( this.isDismiss(reason) ) {
        return null;
      }

      return Promise.reject(reason);
    });
  }
}
